import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X, ArrowRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { LOGO_SRC } from '../constants/branding';
import { KONTAKT_FORM_PATH } from '../constants/navigation';

const navLinks = [
  { path: '/', label: 'Početna' },
  { path: '/usluge', label: 'Usluge' },
  { path: '/o-nama', label: 'O nama' },
  { path: '/projekti', label: 'Projekti' },
  { path: '/kontakt', label: 'Kontakt' },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-primary/5"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            onClick={() => {
              if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2 sm:gap-3 group"
            data-testid="header-logo-link"
          >
            <motion.img 
              src={LOGO_SRC} 
              alt="THERMO COOL SYSTEM logo" 
              className="h-14 w-14 sm:h-12 sm:w-12 object-contain"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            />
            <div className="leading-none select-none">
              <span className="font-brand whitespace-nowrap text-[0.8rem] font-semibold uppercase tracking-[0.14em] text-primary sm:text-[0.86rem]">
                THERMO COOL SYSTEM
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => {
                  if (location.pathname === link.path) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`relative px-4 py-2 font-medium text-sm transition-colors duration-300 rounded-lg ${
                  location.pathname === link.path
                    ? 'text-accent bg-accent/5'
                    : 'text-primary/70 hover:text-primary hover:bg-primary/5'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.div 
                    layoutId="activeNav"
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-accent rounded-full"
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to={KONTAKT_FORM_PATH}
              className="flex items-center gap-2 bg-primary hover:bg-secondary text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
              data-testid="header-contact-cta"
            >
              Javite nam se
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-primary hover:bg-primary/5 rounded-lg transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Zatvori meni' : 'Otvori meni'}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={28} weight="bold" /> : <List size={28} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-primary/5"
            data-testid="mobile-nav"
          >
            <nav className="px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => {
                    setIsOpen(false);
                    window.setTimeout(() => {
                      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                    }, 0);
                  }}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`block py-3 px-4 font-medium rounded-xl transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'text-accent bg-accent/10'
                      : 'text-primary hover:bg-primary/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="pt-4 mt-4 border-t border-primary/10">
                <Link
                  to={KONTAKT_FORM_PATH}
                  onClick={() => {
                    setIsOpen(false);
                    window.setTimeout(() => {
                      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                    }, 0);
                  }}
                  className="flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-xl font-semibold"
                  data-testid="mobile-nav-kontakt"
                >
                  Javite nam se
                  <ArrowRight size={20} weight="bold" />
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;
